import React from 'react'
import {
    MdChatBubbleOutline,
    MdCheck,
    MdContentCopy,
    MdLocationOn,
    MdOutlineEmail,
    MdOutlinePerson,
    MdPhone,
    MdSchedule,
} from 'react-icons/md'
import axios from 'axios'
import './contact-info-tab.css'
import StudentBadge from '../StudentBadge/StudentBadge'
import { baseUrl } from '../../../assets/Animations'
import { getRequestHeaders } from '../../../api'
import { getAssignedTeachersForCard } from '../../../StudentOnboardingFall2023/teacherAssignmentUtils'
import FindFamilyProfileSearchBox from './FindFamilyProfileSearchBox/FindFamilyProfileSearchBox'
import { buildSmsTemplate } from './smsTemplateUtils'
import {
    copySmsTemplateAndRecordAttempt,
    isSmsTemplateExcludedCard,
} from './smsCopyTracking'
import {
    AVAILABILITY_PERIODS,
    buildFamilyDetailRows,
    buildStudentInfoCopyText,
    buildTeacherInfoCopyText,
    formatAvailabilityForCopy,
    getSelectedAvailabilityDays,
} from './contactInfoCopyUtils'

const detailIconById = {
    parent: MdOutlinePerson,
    email: MdOutlineEmail,
    phone: MdPhone,
    address: MdLocationOn,
    message: MdChatBubbleOutline,
}

const ContactInfoTab = ({
    modalInfo,
    setModalInfo,
    setCopyConfirmationPopup,
    familyInfo,
    loadingFamilyInfo,
    hookUpFamily,
    familyLoadError,
    scheduledEvents,
    getFamilyInfo,
    teachers = [],
}) => {
    const [copyingStudentInfo, setCopyingStudentInfo] = React.useState(false)
    const smsTemplateIsExcluded = isSmsTemplateExcludedCard(modalInfo)
    const assignedTeachers = getAssignedTeachersForCard(modalInfo, teachers)
    const familyDetailRows = buildFamilyDetailRows({ familyInfo, modalInfo })
    const selectedAvailabilityDays = getSelectedAvailabilityDays(familyInfo?.initialAvailability)
    const formattedAvailability = formatAvailabilityForCopy(familyInfo?.initialAvailability)

    const showCopyConfirmation = (title) => {
        setCopyConfirmationPopup({ show: true, title })
    }

    const copyText = async (text, successTitle) => {
        if (!String(text || '').trim()) return false
        try {
            await navigator.clipboard.writeText(String(text))
            showCopyConfirmation(successTitle)
            return true
        } catch (error) {
            console.error('Unable to copy family information:', error)
            showCopyConfirmation('Unable to copy. Please check clipboard permission and try again.')
            return false
        }
    }

    const copyPoliciesLink = () => copyText(
        `https://app.atharo.app/student-policies/${modalInfo.familyId}`,
        'Policies link copied!'
    )

    const copyTeacherInfoToClipboard = () => {
        if (assignedTeachers.length === 0) return
        copyText(
            buildTeacherInfoCopyText(assignedTeachers),
            assignedTeachers.length === 1
                ? 'Teacher info copied!'
                : 'Teacher info for all assigned teachers copied!'
        )
    }

    const copySmsTemplateToClipboard = async () => {
        if (smsTemplateIsExcluded) {
            showCopyConfirmation('Day 1 Text is not used for Reassign or Old Student cards.')
            return
        }

        const smsTemplate = buildSmsTemplate({
            familyInfo,
            whichSchool: modalInfo?.whichSchool,
        })

        try {
            await copySmsTemplateAndRecordAttempt({
                smsTemplate,
                familyId: modalInfo?.familyId || familyInfo?.familyId,
                onboardingCardId: modalInfo?.todoId || modalInfo?._id || '',
                writeText: (text) => navigator.clipboard.writeText(text),
                recordAttempt: (payload) => axios.post(
                    `${baseUrl}/inquiry-sms-activity`,
                    payload,
                    getRequestHeaders()
                ),
            })
            showCopyConfirmation('Day 1 Text copied and response time recorded!')
        } catch (error) {
            console.error('Unable to copy or track the Day 1 Text:', error)
            showCopyConfirmation(
                error?.smsTemplateWasCopied
                    ? 'Day 1 Text copied, but response tracking failed. Please copy it again.'
                    : 'The Day 1 Text could not be copied. Please check clipboard permission and try again.'
            )
        }
    }

    const copyStudentInfoToClipboard = async () => {
        if (copyingStudentInfo) return
        setCopyingStudentInfo(true)
        try {
            const response = await axios.get(
                `${baseUrl}/school-management/${modalInfo.familyId}`,
                getRequestHeaders()
            )
            await copyText(buildStudentInfoCopyText(response.data), 'Student info copied!')
        } catch (error) {
            console.error('Unable to load student information for copying:', error)
            showCopyConfirmation('Student info could not be loaded. Please try again.')
        } finally {
            setCopyingStudentInfo(false)
        }
    }

    const renderFamilyDetailRow = (row) => {
        const DetailIcon = detailIconById[row.id] || MdOutlinePerson
        return (
            <div className='family-info-gutter__family-info-chunk' key={row.id}>
                <DetailIcon size={17} aria-hidden='true' />
                <div className='family-info-gutter__family-info-chunk__details__wrapper'>
                    {row.id === 'message' && (
                        <span className='family-info-gutter__family-info-chunk__label'>
                            {row.label}
                        </span>
                    )}
                    <span className='family-info-gutter__family-info-chunk__details'>
                        {row.displayValue || row.value}
                    </span>
                </div>
                <button
                    type='button'
                    className='contact-info-tab__detail-copy'
                    aria-label={`Copy ${row.label}`}
                    title={`Copy ${row.label}`}
                    onClick={() => copyText(row.value, `${row.label} copied!`)}
                >
                    <MdContentCopy size={16} aria-hidden='true' />
                </button>
            </div>
        )
    }

    if (loadingFamilyInfo) {
        return (
            <div className='family-info-gutter__loading-card'>
                <div className='family-info-gutter__loading-icon'>⌛</div>
                <div className='family-info-gutter__loading-title'>Loading family info</div>
                <div className='family-info-gutter__loading-subtitle'>
                    Pulling contact details, students, and notes from the database.
                </div>
                <div className='family-info-gutter__loading-skeleton-group'>
                    <div className='family-info-gutter__loading-skeleton family-info-gutter__loading-skeleton--long' />
                    <div className='family-info-gutter__loading-skeleton family-info-gutter__loading-skeleton--medium' />
                    <div className='family-info-gutter__loading-skeleton family-info-gutter__loading-skeleton--short' />
                </div>
            </div>
        )
    }

    if (hookUpFamily) {
        return (
            <FindFamilyProfileSearchBox
                modalInfo={modalInfo}
                setModalInfo={setModalInfo}
                getFamilyInfo={getFamilyInfo}
            />
        )
    }

    if (!familyInfo) {
        return (
            <FindFamilyProfileSearchBox
                modalInfo={modalInfo}
                setModalInfo={setModalInfo}
                getFamilyInfo={getFamilyInfo}
                missingFamilyId={modalInfo?.familyId}
                loadError={familyLoadError}
            />
        )
    }

    return (
        <div className='contact-info-tab'>
            <div className='contact-info-tab__copy-actions'>
                {!smsTemplateIsExcluded && (
                    <button type='button' onClick={copySmsTemplateToClipboard}>
                        <span>Day 1 Text</span>
                        <MdContentCopy aria-hidden='true' />
                    </button>
                )}
                <button
                    type='button'
                    disabled={assignedTeachers.length === 0}
                    title={assignedTeachers.length === 0 ? 'Assign a teacher to enable this copy button' : ''}
                    onClick={copyTeacherInfoToClipboard}
                >
                    <span>Teacher Info</span>
                    <MdContentCopy aria-hidden='true' />
                </button>
                <button type='button' onClick={copyPoliciesLink}>
                    <span>Policies</span>
                    <MdContentCopy aria-hidden='true' />
                </button>
                <button
                    type='button'
                    disabled={copyingStudentInfo}
                    onClick={copyStudentInfoToClipboard}
                >
                    <span>{copyingStudentInfo ? 'Copying…' : 'Student Info'}</span>
                    <MdContentCopy aria-hidden='true' />
                </button>
            </div>

            <div className='family-info-gutter__scroll-wrapper'>
                <div className='family-info-gutter__student-badge__wrapper'>
                    {(modalInfo.studentList || []).map((student) => (
                        <StudentBadge
                            key={student.studentId || student.studentName}
                            familyInfo={familyInfo}
                            student={student}
                            scheduledEvents={scheduledEvents}
                        />
                    ))}
                </div>

                <div className='family-info-gutter__family-info-area'>
                    {familyDetailRows
                        .filter((row) => row.id !== 'message')
                        .map(renderFamilyDetailRow)}

                    {selectedAvailabilityDays.length > 0 && (
                        <div className='contact-info-tab__availability'>
                            <div className='contact-info-tab__availability-header'>
                                <MdSchedule size={17} aria-hidden='true' />
                                <span className='family-info-gutter__family-info-chunk__label'>
                                    Availability
                                </span>
                                <button
                                    type='button'
                                    className='contact-info-tab__detail-copy'
                                    aria-label='Copy Availability'
                                    title='Copy Availability'
                                    onClick={() => copyText(formattedAvailability, 'Availability copied!')}
                                >
                                    <MdContentCopy size={16} aria-hidden='true' />
                                </button>
                            </div>
                            <div className='contact-info-tab__availability-grid'>
                                <span aria-hidden='true' />
                                {AVAILABILITY_PERIODS.map((period) => (
                                    <span
                                        className='contact-info-tab__availability-period'
                                        key={period}
                                    >
                                        {period}
                                    </span>
                                ))}
                                {selectedAvailabilityDays.map((day) => (
                                    <React.Fragment key={day}>
                                        <span className='contact-info-tab__availability-day'>
                                            {day.slice(0, 3)}
                                        </span>
                                        {AVAILABILITY_PERIODS.map((period) => {
                                            const isAvailable = familyInfo.initialAvailability[day][period] === true
                                            return (
                                                <span
                                                    className={`contact-info-tab__availability-checkbox${isAvailable ? ' contact-info-tab__availability-checkbox--checked' : ''}`}
                                                    role='checkbox'
                                                    aria-checked={isAvailable}
                                                    aria-readonly='true'
                                                    aria-label={`${day} ${period}`}
                                                    key={period}
                                                >
                                                    {isAvailable && <MdCheck size={14} aria-hidden='true' />}
                                                </span>
                                            )
                                        })}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    )}

                    {familyDetailRows
                        .filter((row) => row.id === 'message')
                        .map(renderFamilyDetailRow)}
                </div>
            </div>
        </div>
    )
}

export default ContactInfoTab
